import { useState } from 'react';
import { Box } from '@mui/material';
import { Text } from '@qwickapps/react-framework';

import AgentStatusCard from '../components/dashboard/AgentStatusCard';
import GoalsSummary from '../components/dashboard/GoalsSummary';
import KanbanBoard from '../components/dashboard/KanbanBoard';
import InfrastructurePanel from '../components/dashboard/InfrastructurePanel';
import CostOverview from '../components/dashboard/CostOverview';
import DocumentSearch from '../components/dashboard/DocumentSearch';
import PROverview from '../components/dashboard/PROverview';
import SystemGlobe from '../components/dashboard/SystemGlobe';

type DashboardView = 'overview' | 'agents' | 'goals' | 'infrastructure' | 'costs' | 'documents' | 'code-review';

const mockAgents = [
  { id: '1', name: 'Forge', status: 'busy' as const, currentTask: 'Deploying qwickguard', lastSeen: new Date().toISOString() },
  { id: '2', name: 'Runner', status: 'online' as const, currentTask: 'Executing agent task', lastSeen: new Date().toISOString() },
  { id: '3', name: 'Projects API', status: 'online' as const, currentTask: undefined, lastSeen: new Date().toISOString() },
  { id: '4', name: 'MCP Aggregator', status: 'online' as const, currentTask: undefined, lastSeen: new Date().toISOString() },
];

export default function SDLCDashboardDemo() {
  const [currentView, setCurrentView] = useState<DashboardView>('overview');

  const renderContent = () => {
    switch (currentView) {
      case 'overview':
        return (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
            <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 2', lg: 'span 3' } }}>
              <SystemGlobe />
            </Box>
            <GoalsSummary />
            <CostOverview />
            <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 2', lg: 'span 3' } }}>
              <Text variant="h6" content="Active Agents" />
              <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                {mockAgents.map((agent) => (
                  <AgentStatusCard key={agent.id} agent={agent} />
                ))}
              </Box>
            </Box>
          </Box>
        );
      case 'agents':
        return (
          <Box>
            <Text variant="h5" content="Agent Status" gutterBottom />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
              {mockAgents.map((agent) => (
                <AgentStatusCard key={agent.id} agent={agent} />
              ))}
            </Box>
          </Box>
        );
      case 'goals':
        return (
          <Box>
            <GoalsSummary />
            <Box sx={{ mt: 4 }}>
              <Text variant="h5" content="Task Board" gutterBottom />
              <KanbanBoard />
            </Box>
          </Box>
        );
      case 'infrastructure':
        return <InfrastructurePanel />;
      case 'costs':
        return <CostOverview />;
      case 'documents':
        return <DocumentSearch />;
      case 'code-review':
        return <PROverview />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', p: 0, flexDirection: { xs: 'column', md: 'row' } }}>
      <Box
        sx={{
          width: { xs: '100%', md: 240 },
          display: 'flex',
          flexDirection: { xs: 'row', md: 'column' },
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          borderRight: { xs: 'none', md: '1px solid var(--theme-border)' },
          borderBottom: { xs: '1px solid var(--theme-border)', md: 'none' },
          p: 2,
          bgcolor: 'var(--theme-surface)',
        }}
      >
        <Text variant="h6" content="SDLC Dashboard" gutterBottom customColor="var(--theme-primary)" />
        
        <Box sx={{ mt: 3, display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: 1 }}>
          <SidebarItem label="Overview" icon="dashboard" active={currentView === 'overview'} onClick={() => setCurrentView('overview')} />
          <SidebarItem label="Agents" icon="group" active={currentView === 'agents'} onClick={() => setCurrentView('agents')} />
          <SidebarItem label="Goals & Tasks" icon="flag" active={currentView === 'goals'} onClick={() => setCurrentView('goals')} />
          <SidebarItem label="Infrastructure" icon="cloud" active={currentView === 'infrastructure'} onClick={() => setCurrentView('infrastructure')} />
          <SidebarItem label="Costs" icon="attach_money" active={currentView === 'costs'} onClick={() => setCurrentView('costs')} />
          <SidebarItem label="Documents" icon="description" active={currentView === 'documents'} onClick={() => setCurrentView('documents')} />
          <SidebarItem label="Code Review" icon="code" active={currentView === 'code-review'} onClick={() => setCurrentView('code-review')} />
        </Box>
      </Box>

      <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
        {renderContent()}
      </Box>
    </Box>
  );
}

function SidebarItem({ label, icon, active, onClick }: { label: string; icon: string; active: boolean; onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.5,
        borderRadius: 1,
        cursor: 'pointer',
        bgcolor: active ? 'var(--theme-primary)' : 'transparent',
        color: active ? 'white' : 'var(--theme-text)',
        '&:hover': { bgcolor: active ? 'var(--theme-primary)' : 'var(--theme-surface-hover)' },
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: { xs: 'auto', md: 180 },
        flex: { xs: '1 0 auto', md: 'none' },
      }}
    >
      <span className="material-icons" style={{ fontSize: 18 }}>{icon}</span>
      <Text variant="body2" content={label} />
    </Box>
  );
}
